/**
 * Onboarding in linea (QA26-F5.2, STD-6 punto 2): presentazione al primo accesso per ruolo e suggerimenti contestuali
 * agganciati a `data-tip="<anchor>"`. Qui solo testi, ancore e regole: li monta @brandart/help-kit nel frontend
 * (frontend/src/aiuto/), lo stato «già visto» sta sul server (GET/PUT /api/me/ui-prefs, chiavi onboarding.<ruolo>,
 * tip.<anchor> e tip.disabled).
 *
 * Regole: al massimo 8 schede per ruolo; ogni ruolo ha la sua presentazione (chi cambia ruolo vede quella del ruolo
 * nuovo); `VERSIONE_PRESENTAZIONE` sale quando la presentazione cambia in modo sostanziale e la ripropone a tutti. Un
 * suggerimento alla volta, mai sopra una modale. Le ancore sono chiavi salvate sul server: non si rinominano.
 *
 * Le schermate (N3) mettono `data-tip={ANCORA.x}` sugli elementi: le ancore della testata e di Nuova segnalazione e
 * Classificazione sono quelle del mockup v2.
 */
import { puo, RUOLI, type Azione, type Ruolo, type RuoloPortale, type Testo } from "./dominio";
import type { Vista } from "./usage";

/**
 * 1 (02/11/2026): prima release in prova. 2 (03/11/2026): «Lascia un feedback» nell'ultima scheda (FBK26-F6.4).
 * 3 (03/11/2026): la segnalazione non è una NC; fonte, allegati, azioni ISO 9001, tempi PRO 09 (QA26-F4.9..F4.11).
 */
export const VERSIONE_PRESENTAZIONE = 3;
export const MAX_SCHEDE = 8;

export type Figura = "benvenuto" | "percorso" | "ruolo" | "segnala" | "nc" | "claim" | "report" | "proposte" | "assistente" | "guida";

export interface SchedaPresentazione {
  figura?: Figura;
  titolo: Testo;
  testo: Testo;
}

const BENVENUTO: SchedaPresentazione = {
  figura: "benvenuto",
  titolo: { it: "Benvenuto nel Portale Qualità", en: "Welcome to the Quality Portal" },
  testo: {
    it: "Qui ogni criticità di un cliente diventa una segnalazione con azioni assegnate: una correttiva subito e una preventiva perché non accada più.",
    en: "Here every customer issue becomes a report with assigned actions: an immediate corrective one and a preventive one so that it does not happen again.",
  },
};
const PERCORSO: SchedaPresentazione = {
  figura: "percorso",
  titolo: { it: "Dalla segnalazione alla chiusura", en: "From report to closure" },
  testo: {
    it: "Segnalata, presa in carico, azioni in corso, chiusa dopo la verifica col cliente. Il cliente va aggiornato entro 2 giorni lavorativi, poi ogni 5 (VIP ogni 2).",
    en: "Reported, taken in charge, actions in progress, closed after checking with the customer. The customer gets an update within 2 working days, then every 5 (VIP every 2).",
  },
};
const PROPOSTE: SchedaPresentazione = {
  figura: "proposte",
  titolo: { it: "Chiunque propone, l'admin decide", en: "Anyone proposes, the admin decides" },
  testo: {
    it: "Tipo e cause, collegamenti fra claim e segnalazioni, voci nuove delle liste: tu proponi, l'admin approva. La segnalazione non è una NC: se serve, la qualità ne apre una a mano.",
    en: "Type and causes, links between claims and reports, new list items: you propose, the admin approves. A report is not an NC: if needed, quality opens one by hand.",
  },
};
const ASSISTENTE: SchedaPresentazione = {
  figura: "assistente",
  titolo: { it: "L'Assistente", en: "The Assistant" },
  testo: {
    it: "Chiedi in italiano o in inglese: «quali NC sono scadute?». È un assistente AI: vede solo quello che vedi tu e non modifica niente.",
    en: "Ask in Italian or English: \"which NCs are overdue?\". It is an AI assistant: it sees only what you see and changes nothing.",
  },
};
const GUIDA: SchedaPresentazione = {
  figura: "guida",
  titolo: { it: "La Guida è sempre qui", en: "The Guide is always here" },
  testo: {
    it: "In alto a destra trovi la Guida, a partire dal tuo ruolo, con glossario e domande frequenti. Da lì rivedi questa presentazione. Qualcosa non va o hai un'idea? Il pulsante rotondo in basso a destra, «Lascia un feedback».",
    en: "At the top right you find the Guide, starting from your role, with glossary and FAQ. From there you can replay this introduction. Something wrong or an idea? The round button at the bottom right, \"Leave feedback\".",
  },
};

export const PRESENTAZIONE: Record<Ruolo, readonly SchedaPresentazione[]> = {
  commerciale: [
    BENVENUTO,
    PERCORSO,
    {
      figura: "segnala",
      titolo: { it: "Segnala subito", en: "Report straight away" },
      testo: {
        it: "Appena un cliente si lamenta apri una segnalazione: fonte (mail, telefono, Salesforce…), problema, impatto sul cliente, severità. Allega la mail così com'è, anche .msg di Outlook.",
        en: "As soon as a customer complains, open a report: source (email, phone, Salesforce…), problem, impact on the customer, severity. Attach the email as it is, Outlook .msg too.",
      },
    },
    {
      figura: "segnala",
      titolo: { it: "Tienila aggiornata", en: "Keep it up to date" },
      testo: {
        it: "Registra ogni aggiornamento dato al cliente: la PRO 09 chiede il primo entro 2 giorni lavorativi, poi ogni 5 (VIP ogni 2). Se sei in ritardo lo vedi nella home.",
        en: "Log every update you give the customer: PRO 09 asks for the first within 2 working days, then every 5 (VIP every 2). If you are late you see it on the home page.",
      },
    },
    PROPOSTE,
    {
      figura: "ruolo",
      titolo: { it: "Cosa vedi", en: "What you see" },
      testo: {
        it: "Vedi segnalazioni, NC e report. I claim ai fornitori e le schede fornitore restano all'amministrazione e alla qualità.",
        en: "You see reports, NCs and the weekly report. Supplier claims and supplier pages stay with accounting and quality.",
      },
    },
    ASSISTENTE,
    GUIDA,
  ],
  qualita: [
    BENVENUTO,
    PERCORSO,
    {
      figura: "proposte",
      titolo: { it: "Tipo, azioni e NC", en: "Type, actions and NCs" },
      testo: {
        it: "Prendi in carico, crea l'azione correttiva immediata e la preventiva con funzione, persona e scadenza; le verifichi tu. La NC verso il fornitore o interna la apri a mano, solo se serve.",
        en: "Take it in charge, create the immediate corrective and the preventive action with function, person and due date; you verify them. You open the supplier or internal NC by hand, only if needed.",
      },
    },
    {
      figura: "nc",
      titolo: { it: "8D leggero", en: "Light 8D" },
      testo: {
        it: "D3 è l'azione nel breve, D5 quella di lungo termine, D8 la verifica dell'efficacia. Ogni NC ha un owner e una scadenza.",
        en: "D3 is the short term action, D5 the long term action, D8 the effectiveness check. Every NC has an owner and a due date.",
      },
    },
    {
      figura: "nc",
      titolo: { it: "Chiudere una NC", en: "Closing an NC" },
      testo: {
        it: "Si chiude solo con l'azione di lungo termine compilata e la verifica dell'efficacia «Efficace». Altrimenti resta aperta.",
        en: "It closes only with the long term action filled in and an \"Effective\" effectiveness check. Otherwise it stays open.",
      },
    },
    {
      figura: "claim",
      titolo: { it: "Costo della non qualità", en: "Cost of poor quality" },
      testo: {
        it: "Registra i costi per voce: rilavorazione, aereo, rimborso, selezione. Se si recuperano dal fornitore, nasce un claim.",
        en: "Record the costs by item: rework, air freight, refund, sorting. If they can be recovered from the supplier, a claim follows.",
      },
    },
    ASSISTENTE,
    GUIDA,
  ],
  amministrazione: [
    BENVENUTO,
    {
      figura: "claim",
      titolo: { it: "I claim ai fornitori", en: "Supplier claims" },
      testo: {
        it: "La sezione Claim fornitori sostituisce il riepilogo: importo in USD o EUR, stato, modalità di recupero, importo recuperato.",
        en: "The Supplier claims section replaces the summary file: amount in USD or EUR, status, recovery method, amount recovered.",
      },
    },
    {
      figura: "claim",
      titolo: { it: "Blocco pagamento", en: "Payment hold" },
      testo: {
        it: "Segna quando i pagamenti al fornitore sono fermi finché il claim non si chiude: compare nei numeri in alto.",
        en: "Mark when payments to the supplier are on hold until the claim closes: it shows in the figures at the top.",
      },
    },
    {
      figura: "proposte",
      titolo: { it: "Claim e segnalazioni", en: "Claims and reports" },
      testo: {
        it: "Il collegamento fra un claim e una segnalazione passa da una proposta, che decide l'admin.",
        en: "The link between a claim and a report goes through a proposal, decided by the admin.",
      },
    },
    {
      figura: "nc",
      titolo: { it: "Costo della non qualità", en: "Cost of poor quality" },
      testo: {
        it: "Vedi e registri i costi delle NC per voce: dicono quanto costa ogni problema e quanto si recupera.",
        en: "You see and record NC costs by item: they tell how much each problem costs and how much is recovered.",
      },
    },
    ASSISTENTE,
    GUIDA,
  ],
  direzione: [
    BENVENUTO,
    PERCORSO,
    {
      figura: "report",
      titolo: { it: "Il cruscotto", en: "The dashboard" },
      testo: {
        it: "Aperti, urgenti e critici, valore a rischio, costo della non qualità, temi ricorrenti e fornitori con più NC.",
        en: "Open, urgent and critical, value at risk, cost of poor quality, recurring themes and suppliers with the most NCs.",
      },
    },
    {
      figura: "report",
      titolo: { it: "Il report settimanale", en: "The weekly report" },
      testo: {
        it: "Nuove e aggiornate della settimana, avanzamento delle NC, azioni scadute, con il testo della mail e l'Excel per l'ExCo.",
        en: "New and updated reports of the week, NC progress, overdue actions, with the email text and the Excel file for the ExCo.",
      },
    },
    ASSISTENTE,
    GUIDA,
  ],
  admin: [
    BENVENUTO,
    PERCORSO,
    {
      figura: "proposte",
      titolo: { it: "Decidi le proposte", en: "Decide the proposals" },
      testo: {
        it: "Approva, modifica e approva o rifiuta, anche più proposte insieme. «Tipo e cause» approvati non aprono NC. Le proposte dell'import sono «Import (Leo)».",
        en: "Approve, change and approve or reject, several at once too. An approved classification opens the NCs. Import proposals show as \"Import (Leo)\".",
      },
    },
    {
      figura: "ruolo",
      titolo: { it: "Accessi", en: "Access" },
      testo: {
        it: "I ruoli vengono dall'app Entra, uno per sezione (qa.admin, redflag.<ruolo>, nc.<ruolo>). Le richieste di accesso arrivano qui: assegni il ruolo in Entra e segni la richiesta come gestita.",
        en: "Roles come from the Entra app, one per section (qa.admin, redflag.<role>, nc.<role>). Access requests land here: assign the role in Entra and mark the request as handled.",
      },
    },
    {
      figura: "ruolo",
      titolo: { it: "Liste, criteri e registro", en: "Lists, criteria and log" },
      testo: {
        it: "Liste dei form, criteri di severità e cambio USD→EUR. Il registro dice chi ha fatto cosa, senza i valori.",
        en: "Form lists, severity criteria and USD→EUR rate. The log tells who did what, without the values.",
      },
    },
    ASSISTENTE,
    GUIDA,
  ],
};

/* ---------- suggerimenti ---------- */

/** Valori di `data-tip`. Chiavi salvate sul server (`tip.<anchor>`): non si rinominano. */
export const ANCORA = {
  cerca: "cerca",
  assistente: "assistente",
  guida: "guida",
  nuova: "nuova",
  classifica: "classifica",
  azioni: "rf.azioni",
  allegati: "rf.allegati",
  pro09: "rf.pro09",
  aggiornamenti: "rf.aggiornamenti",
  ncOwner: "nc.owner",
  ncChiudi: "nc.chiudi",
  ncCosti: "nc.costi",
  claimBlocco: "claim.blocco",
  claimCollega: "claim.collega",
  proposteApprova: "proposte.approva",
  reportMail: "report.mail",
} as const;
export type Ancora = (typeof ANCORA)[keyof typeof ANCORA];

export interface Suggerimento {
  anchor: Ancora;
  /** Vista in cui compare (la stessa dei KPI). */
  vista: Vista;
  /** Permesso che serve per vederlo (null = tutti). */
  serve: Azione | null;
  titolo: Testo;
  testo: Testo;
  /** Domanda già scritta per «Chiedi all'assistente». */
  domanda?: Testo;
}

/** Ordine = priorità nella stessa vista: compare il primo non visto il cui elemento è sullo schermo. */
export const SUGGERIMENTI: readonly Suggerimento[] = [
  {
    anchor: ANCORA.cerca,
    vista: "home",
    serve: null,
    titolo: { it: "Cerca ovunque", en: "Search everywhere" },
    testo: { it: "Cliente, fornitore o codice RF, NC, claim: un campo solo per tutto il portale.", en: "Customer, supplier or RF, NC, claim code: one field for the whole portal." },
  },
  {
    anchor: ANCORA.assistente,
    vista: "home",
    serve: null,
    titolo: { it: "Chiedi all'Assistente", en: "Ask the Assistant" },
    testo: { it: "Domande sui dati che vedi, in italiano o in inglese. Legge soltanto.", en: "Questions on the data you see, in Italian or English. It only reads." },
    domanda: { it: "Quali NC sono scadute?", en: "Which NCs are overdue?" },
  },
  {
    anchor: ANCORA.guida,
    vista: "home",
    serve: null,
    titolo: { it: "Guida", en: "Guide" },
    testo: { it: "Istruzioni per il tuo ruolo, glossario e domande frequenti. Da qui rivedi la presentazione.", en: "Instructions for your role, glossary and FAQ. From here you can replay the introduction." },
  },
  {
    anchor: ANCORA.nuova,
    vista: "redflag",
    serve: "rf.crea",
    titolo: { it: "Segnala subito", en: "Report straight away" },
    testo: { it: "Ogni ritardo, anche lieve, va segnalato appena lo sai.", en: "Any delay, even a small one, should be reported as soon as you know." },
    domanda: { it: "Che severità do a una segnalazione?", en: "What severity should I give a report?" },
  },
  {
    anchor: ANCORA.classifica,
    vista: "segnalazione",
    serve: "proposta.crea",
    titolo: { it: "Tipo e cause", en: "Type and causes" },
    testo: { it: "Proponi il tipo di segnalazione e le cause: l'admin approva. Approvarli non apre NC.", en: "Propose the report type and the causes: the admin approves. Approving them opens no NC." },
    domanda: { it: "Perché questa segnalazione non ha aperto una NC?", en: "Why did this report not open an NC?" },
  },
  {
    anchor: ANCORA.azioni,
    vista: "segnalazione",
    serve: "rf.vedi",
    titolo: { it: "Azioni ISO 9001", en: "ISO 9001 actions" },
    testo: {
      it: "Correttiva immediata e preventiva, ognuna con funzione, persona e scadenza. Chi ne è owner la ritrova in «Le mie azioni».",
      en: "Immediate corrective and preventive, each with function, person and due date. Its owner finds it in \"My actions\".",
    },
    domanda: { it: "Quali azioni sono scadute?", en: "Which actions are overdue?" },
  },
  {
    anchor: ANCORA.pro09,
    vista: "segnalazione",
    serve: "rf.vedi",
    titolo: { it: "Tempi col cliente", en: "Customer timing" },
    testo: {
      it: "PRO 09: primo aggiornamento al cliente entro 2 giorni lavorativi, poi ogni 5 (VIP ogni 2). Registralo qui e il conteggio riparte.",
      en: "PRO 09: first update to the customer within 2 working days, then every 5 (VIP every 2). Log it here and the count restarts.",
    },
    domanda: { it: "Quali segnalazioni sono in ritardo con il cliente?", en: "Which reports are late with the customer?" },
  },
  {
    anchor: ANCORA.allegati,
    vista: "segnalazione",
    serve: "rf.vedi",
    titolo: { it: "Allegati", en: "Attachments" },
    testo: {
      it: "Trascina la mail del cliente (.msg o .eml): mittente, oggetto e testo si leggono qui, e i suoi allegati diventano allegati della segnalazione.",
      en: "Drop the customer's email (.msg or .eml): sender, subject and text show here, and its attachments become attachments of the report.",
    },
  },
  {
    anchor: ANCORA.aggiornamenti,
    vista: "segnalazione",
    serve: "rf.modifica",
    titolo: { it: "Aggiornamenti", en: "Updates" },
    testo: { it: "Ogni novità dal cliente va qui. Dopo 7 giorni senza aggiornamenti la segnalazione diventa «Da aggiornare».", en: "Every update from the customer goes here. After 7 days without updates the report becomes \"To be updated\"." },
  },
  {
    anchor: ANCORA.ncOwner,
    vista: "nc",
    serve: "nc.lavora",
    titolo: { it: "Owner e scadenza", en: "Owner and due date" },
    testo: { it: "Una NC senza owner o con la scadenza passata finisce in «Da fare» e nel report.", en: "An NC without an owner or past its due date shows in \"To do\" and in the weekly report." },
    domanda: { it: "Quali NC non hanno ancora un owner?", en: "Which NCs have no owner yet?" },
  },
  {
    anchor: ANCORA.ncChiudi,
    vista: "nc",
    serve: "nc.lavora",
    titolo: { it: "Chiudi la NC", en: "Close the NC" },
    testo: { it: "Servono l'azione di lungo termine (D5) e la verifica dell'efficacia (D8) con esito «Efficace».", en: "It needs the long term action (D5) and an \"Effective\" effectiveness check (D8)." },
    domanda: { it: "Perché non riesco a chiudere questa NC?", en: "Why can't I close this NC?" },
  },
  {
    anchor: ANCORA.ncCosti,
    vista: "nc",
    serve: "nc.costi",
    titolo: { it: "Costo della non qualità", en: "Cost of poor quality" },
    testo: { it: "Per voce: rilavorazione, aereo, rimborso, selezione. La segnalazione mostra la somma.", en: "By item: rework, air freight, refund, sorting. The report shows the total." },
  },
  {
    anchor: ANCORA.claimBlocco,
    vista: "claim",
    serve: "claim.gestisci",
    titolo: { it: "Blocco pagamento", en: "Payment hold" },
    testo: { it: "Segna quando i pagamenti al fornitore restano fermi finché il claim non si chiude.", en: "Mark when payments to the supplier stay on hold until the claim closes." },
  },
  {
    anchor: ANCORA.claimCollega,
    vista: "claim",
    serve: "claim.vedi",
    titolo: { it: "Collega a una segnalazione", en: "Link to a report" },
    testo: { it: "Il collegamento è una proposta: lo approva l'admin.", en: "The link is a proposal: the admin approves it." },
  },
  {
    anchor: ANCORA.proposteApprova,
    vista: "proposte",
    serve: "proposta.decidi",
    titolo: { it: "Approva insieme", en: "Approve together" },
    testo: { it: "Seleziona più proposte e approvale in un colpo: quelle che non passano restano in elenco con il motivo.", en: "Select several proposals and approve them at once: those that fail stay in the list with the reason." },
  },
  {
    anchor: ANCORA.reportMail,
    vista: "report",
    serve: "report.vedi",
    titolo: { it: "Testo della mail", en: "Email text" },
    testo: { it: "Il testo del report è pronto da copiare; l'Excel ha le colonne del vecchio file più owner, azioni e NC.", en: "The report text is ready to copy; the Excel file has the old file's columns plus owners, actions and NCs." },
  },
];

export const suggerimentiPer = (ruoli: readonly RuoloPortale[]) => SUGGERIMENTI.filter((s) => !s.serve || puo(ruoli, s.serve));

/** Chiavi di ui-prefs dell'aiuto: onboarding.<ruolo>, tip.<anchor>, tip.disabled. */
export const chiaviAiuto = (): string[] => [...RUOLI.map((r) => `onboarding.${r}`), ...SUGGERIMENTI.map((s) => `tip.${s.anchor}`), "tip.disabled"];
